import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { AgGridModule } from 'ag-grid-angular';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { AttachmentViewerComponent } from '../../secure/entity-viewers/attachment-viewer/attachment-viewer.component';
import { NewAttachmentComponent } from '../../create-new/new-attachment/new-attachment.component';

import { AttachmentsService } from '../../service/files-and-attachments/attachments.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';
import { EntityType } from '../../objects/interface-library';

import * as attachmentsColumns from '../../shared/entity-columns/attachments-columns';
import { EntityList } from '../../objects/entity-list';

/**
 * Grid overlays for the two empty cases. Deliberately different text: "nobody
 * has added one" and "there are some but not for you" are different facts, and
 * a single vague message sends the user to support for the wrong reason.
 */
const ATTACHMENTS_EMPTY_TEMPLATE = `
  <div class="attachments-overlay">
    <div class="attachments-overlay-title">No attachments yet</div>
    <p>
      Attachments are documents added to a contract, BAA, COI or party record —
      an amendment, a certificate, a signed addendum.
    </p>
    <p>
      A document uploaded when a contract was created stays on that contract and
      is opened from there; it does not appear in this list.
    </p>
  </div>`;

const ATTACHMENTS_HIDDEN_TEMPLATE = `
  <div class="attachments-overlay">
    <div class="attachments-overlay-title">No attachments you have access to</div>
    <p>
      There are attachments in this organization, but they belong to contracts
      outside the sites, legal entities or contract types assigned to you.
    </p>
  </div>`;

@Component({
  selector: 'app-attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatDividerModule,
    MatSidenavModule,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule
  ]
})
export class AttachmentsComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsStore = inject(PermissionsStoreService);
  private cdr = inject(ChangeDetectorRef);
  public attachmentsService = inject(AttachmentsService);

  @ViewChild('attachmentsSidenavActions') attachmentsSidenavActions!: TemplateRef<any>;

  /**
   * Attachments the signed-in user is allowed to see, enriched with entity
   * display names.
   *
   * Sourced from `visibleContracts$` + `archivedContracts$` — the caches the
   * ContractService already filters by role, metadata limits (sites / legal
   * entities / contract types) and ownership — NOT from `allContracts$`. An
   * attachment is a document on a record; if you can't see the record you must
   * not see its documents, and this page previously showed every row in the
   * table to everyone. That was invisible only because the table happened to be
   * empty for the live customer.
   *
   * Both contract caches are needed: `visibleContracts$` is active-only, and an
   * attachment on an ARCHIVED contract must stay visible to someone permitted to
   * see it — archival is not a permission decision.
   */
  allAttachments$: Observable<any[]> = combineLatest([
    this.attachmentsService.allAttachments$,
    this.contractService.visibleContracts$,
    this.contractService.archivedContracts$,
    this.partyService.allParties$
  ]).pipe(
    map(([attachments, visibleContracts, archivedContracts, parties]) => {
      const permittedContracts = [...visibleContracts, ...archivedContracts];
      const permittedIds = new Set(
        permittedContracts.map((c: any) => c.contractId).filter(Boolean)
      );
      const permitted = attachments.filter(att => this.isPermittedAttachment(att, permittedIds));

      // Kept for the empty-state wording: "nothing here yet" and "nothing you can
      // see" are different facts and must not read the same to the user.
      this.totalAttachmentCount = attachments.length;
      this.visibleAttachmentCount = permitted.length;
      this.applyGridOverlay();

      return permitted.map(att => this.enrichAttachment(att, permittedContracts, parties));
    })
  );

  /** Row counts behind the empty state — see allAttachments$. */
  private totalAttachmentCount = 0;
  private visibleAttachmentCount = 0;
  /** False until the attachments cache has finished its first load. */
  private attachmentsLoaded = false;
  private cacheRefreshedSub?: Subscription;

  // UI state
  columnChooserControl = new FormControl();
  customizeColumnsUi = false;
  waitingForServer = false;
  showNameSavedView = false;
  isInitializing = true;

  // Permissions
  isViewer = this.permissionsStore.isViewer;

  // Column configuration
  defaultCols: any[] = [
    attachmentsColumns.colAttachmentName,
    attachmentsColumns.colAttachmentDescription,
    attachmentsColumns.colType,
    attachmentsColumns.colOwner,
    attachmentsColumns.colAssociatedEntityType,
    attachmentsColumns.colAssociatedEntityViewValue,
    attachmentsColumns.colAssociatedEntityId
  ];

  selectedCols = this.defaultCols;

  // Column customization properties
  availableColumns: any[] = [];
  availableColumnSelectList: { value: any; viewValue: string }[] = [];
  columnSelectorControl = new FormControl<string>('');
  selectedColumnsList: any[] = [];
  filteredColumns$!: Observable<{ value: any; viewValue: string }[]>;

  // AG-Grid default column configuration
  public defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab']
  };
  public showFloatingFilters = false;

  // Enable tooltips
  public tooltipShowDelay = 300;
  public tooltipHideDelay = 10000;
  public tooltipInteraction = true;

  constructor(public dialog: MatDialog) {
    super();
    this.entityType = 'attachments';
    this.buildAvailableColumns();
    this.initializeSessionStorage();
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Attachments');

    // Trigger data load
    this.attachmentsService.refreshAllAttachmentsRecords();

    // Drives the loading-vs-empty distinction on the grid overlay: an empty grid
    // during the first fetch must not claim there are no attachments.
    this.cacheRefreshedSub = this.attachmentsService.isCacheRefreshed$.subscribe(loaded => {
      this.attachmentsLoaded = loaded;
      this.applyGridOverlay();
    });

    // Set up autocomplete filtering for column selector
    this.filteredColumns$ = this.columnSelectorControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterColumns(value || ''))
    );
  }

  /**
   * Override onGridReady to complete initialization after grid is ready
   */
  override onGridReady(params: any): void {
    super.onGridReady(params);

    // The data may have arrived before the grid did — state is held on the
    // component, so re-apply it now that there's an api to apply it to.
    this.applyGridOverlay();

    this.completeInitialization().catch(err => {
      console.error('[onGridReady] Error during initialization:', err);
      this.isInitializing = false;
    });
  }

  /**
   * Completes the grid initialization after grid is ready.
   */
  private async completeInitialization(): Promise<void> {
    this.suspendSessionTracking = true;

    const hasSessionState = this.sessionStorageKey && sessionStorage.getItem(this.sessionStorageKey);

    if (hasSessionState) {
      this.loadSessionState();
      this.suspendSessionTracking = false;
    } else {
      await this.loadUserViews();

      const hasNewFormatView = this.savedNamedViews && (this.savedNamedViews as any).gridState;
      const hasOldFormatView =
        this.savedNamedViews && ((this.savedNamedViews as any).columnState || (this.savedNamedViews as any).savedViewData);

      if (hasNewFormatView || hasOldFormatView) {
        await this.loadNamedView(this.savedNamedViews);
        this.suspendSessionTracking = false;
      } else {
        this.autoSizeAll();
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }

    this.isInitializing = false;
    this.cdr.detectChanges();
  }

  /**
   * Builds the list of available columns
   */
  private buildAvailableColumns(): void {
    this.availableColumns = [...attachmentsColumns.allColumns];

    // Filter out non-column objects
    this.availableColumns = this.availableColumns.filter(col => col && col.colId && col.headerName);

    // Remove duplicates by colId
    const uniqueColsMap = new Map();
    this.availableColumns.forEach(col => {
      if (!uniqueColsMap.has(col.colId)) {
        uniqueColsMap.set(col.colId, col);
      }
    });
    this.availableColumns = Array.from(uniqueColsMap.values());

    // Build and sort select list
    this.availableColumnSelectList = this.availableColumns.map(col => ({
      value: col,
      viewValue: col.headerName
    }));
    this.availableColumnSelectList.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  /**
   * Filters available columns based on user input
   */
  private _filterColumns(value: string | any): { value: any; viewValue: string }[] {
    if (!value || typeof value !== 'string') {
      return this.availableColumnSelectList;
    }
    const filterValue = value.toLowerCase();
    return this.availableColumnSelectList.filter(option => option.viewValue.toLowerCase().includes(filterValue));
  }

  /**
   * Display function for autocomplete - returns empty string to keep input clear
   */
  displayColumnFn(): string {
    return '';
  }

  ngAfterViewInit(): void {
    this.sidenavContentService.updateSidenavContent(this.attachmentsSidenavActions);
  }

  ngOnDestroy(): void {
    this.sidenavContentService.updateSidenavContent(null);
    this.cacheRefreshedSub?.unsubscribe();
  }

  // Grid events
  onCellClicked(event: any): void {
    console.log('Attachment clicked:', event.data);
    this.openAttachmentViewer(event.data);
  }

  openAttachmentViewer(attachment: any): void {
    this.dialog.open(AttachmentViewerComponent, {
      width: '1000px',
      height: '500px',
      data: { attachment }
    });
  }

  addTemplate(): void {
    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '800px',
      data: {
        message: 'New templates are used when creating a new contract.',
        title: 'Add New Template',
        attachmentType: 'Approved Template'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.attachmentsService.refreshAllAttachmentsRecords();
      }
    });
  }

  /**
   * May this user see this attachment?
   *
   * Contract-associated rows are gated on the permitted-contract set (which the
   * ContractService derives from the user's role and metadata limits), so this
   * page can't become a side door to documents on contracts the user is blocked
   * from opening.
   *
   * Everything else passes through. Parties, BAAs, COIs and people have NO
   * per-record visibility model anywhere in the app — their list pages show
   * every row to everyone — so filtering their attachments here would invent a
   * rule that exists in exactly one place, and hide documents the same user can
   * reach from the record itself. If per-party visibility ever lands, this is
   * the function that has to learn about it.
   *
   * `associatedEntityType` is compared case-insensitively: live rows carry
   * 'contract' (lowercase) while the EntityType enum says 'Contract'.
   */
  private isPermittedAttachment(attachment: any, permittedContractIds: Set<string>): boolean {
    const entityType = String(attachment?.associatedEntityType ?? '').toLowerCase();
    if (entityType !== String(EntityType.Contract).toLowerCase() && entityType !== 'contract') {
      return true;
    }
    const entityId = attachment?.associatedEntityId;
    // A contract attachment with no entity id can't be checked; keep it rather
    // than silently disappearing a document nobody could then find.
    if (!entityId) return true;
    return permittedContractIds.has(entityId);
  }

  /**
   * Show the right thing when the grid has no rows. An empty 800px grid reads as
   * broken — which is exactly how the live customer's empty attachments table has
   * been reported to us — so say which of the three situations it actually is.
   */
  private applyGridOverlay(): void {
    if (!this.gridApi) return;

    if (!this.attachmentsLoaded) {
      this.gridApi.showLoadingOverlay();
      return;
    }
    if (this.visibleAttachmentCount > 0) {
      this.gridApi.hideOverlay();
      return;
    }

    this.gridApi.setGridOption(
      'overlayNoRowsTemplate',
      this.totalAttachmentCount > 0 ? ATTACHMENTS_HIDDEN_TEMPLATE : ATTACHMENTS_EMPTY_TEMPLATE
    );
    this.gridApi.showNoRowsOverlay();
  }

  /**
   * Enriches an attachment with entity display names if not already present.
   */
  private enrichAttachment(attachment: any, contracts: any[], parties: any[]): any {
    if (attachment.associatedEntityViewValue && attachment.contractFriendlyName) {
      return attachment;
    }

    const enriched = { ...attachment };
    const entityId = attachment.associatedEntityId;
    const entityType = attachment.associatedEntityType;

    if (!entityId || enriched.associatedEntityViewValue) {
      return enriched;
    }

    let displayName = '';

    if (entityType === EntityType.Contract || entityType === 'Contract') {
      const contract = contracts.find((c: any) => c.contractId === entityId);
      displayName = contract?.nameOfContract || contract?.friendlyName || entityId;
      enriched.contractFriendlyName = displayName;
    } else if (entityType === EntityType.Party || entityType === 'Party') {
      const party = parties.find((p: any) => p.vendorNum === entityId);
      displayName = party?.name || entityId;
    } else {
      displayName = entityId;
    }

    enriched.associatedEntityViewValue = displayName;
    return enriched;
  }

  onSearchChange(searchText: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchText);
    }
  }

  // Column management methods
  selectColumn(column: any): void {
    if (column && !this.selectedColumnsList.find(c => c.colId === column.colId)) {
      this.selectedColumnsList.push(column);
      setTimeout(() => {
        this.columnSelectorControl.setValue('', { emitEvent: false });
      }, 0);
    }
  }

  removeColumn(column: any): void {
    const index = this.selectedColumnsList.findIndex(c => c.colId === column.colId);
    if (index >= 0) {
      this.selectedColumnsList.splice(index, 1);
    }
  }

  applyColumns(): void {
    if (this.gridApi && this.selectedColumnsList.length > 0) {
      this.selectedCols = this.selectedColumnsList;
      this.gridApi.setGridOption('columnDefs', this.selectedCols);
      this.customizeColumnsUi = false;
      this.markSessionAsModified();
    }
  }

  openCustomizeColumnsDialog(): void {
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      const visibleColIds = currentState.filter((col: any) => !col.hide).map((col: any) => col.colId);

      this.selectedColumnsList = this.availableColumns.filter(col => visibleColIds.includes(col.colId));
    } else {
      this.selectedColumnsList = [...this.selectedCols];
    }

    this.customizeColumnsUi = true;
  }

  toggleFloatingFilters(): void {
    this.showFloatingFilters = !this.showFloatingFilters;

    if (this.gridApi) {
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });

      allColumnIds.forEach(colId => {
        this.gridApi.setColumnFilterProperty(colId, 'floatingFilter', this.showFloatingFilters);
      });

      this.gridApi.refreshHeader();
    }
  }

  // Export functions
  exportCSV(): void {
    if (this.gridApi) {
      const params = {
        processCellCallback: this.formattingFunction,
        fileName: 'attachments_export.csv'
      };
      this.gridApi.exportDataAsCsv(params);
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      const excelParams = {
        processCellCallback: this.formattingFunction,
        fileName: 'attachments_export.xlsx'
      };
      this.gridApi.exportDataAsExcel(excelParams);
    }
  }
}
